/**
 * AddToCalendar
 * =============
 * Calendar integration component for adding UnityLove events to user calendars.
 * 
 * Cherry #3: Calendar + Habit Loop
 * 
 * Entry Points (ONLY):
 * - After ticket purchase success
 * - After joining an event
 * - Secondary CTA on Post-Event Momentum Screen
 * 
 * Rules (LOCKED):
 * - No marketing copy in calendar entries
 * - No auto-adds (user must explicitly click)
 * - Does NOT affect minutes or access rules
 */

import React, { useState, useCallback } from "react";
import { Calendar, ChevronDown, ExternalLink } from "lucide-react";
import { track } from "../utils/analytics";

const BACKEND_URL = window.location.origin;

// Calendar provider options
const PROVIDERS = {
  google: {
    id: "google",
    label: "Google Calendar",
    icon: "G",
    color: "bg-blue-500/20 text-blue-400",
  },
  apple: {
    id: "apple",
    label: "Apple Calendar",
    icon: "",
    color: "bg-gray-500/20 text-gray-300",
  },
  outlook: {
    id: "outlook",
    label: "Outlook",
    icon: "O",
    color: "bg-blue-600/20 text-blue-400",
  },
};

/**
 * Generate calendar links for an event
 */
async function getCalendarLinks(eventId, token) {
  const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/calendar`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error("Failed to get calendar links");
  }

  return response.json();
}

/**
 * Compact "Add to Calendar" button with dropdown
 */
export default function AddToCalendar({ 
  eventId, 
  eventTitle,
  token,
  variant = "default", // "default" | "compact" | "inline"
  className = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [links, setLinks] = useState(null);

  const handleToggle = useCallback(async () => {
    track("calendar_add_clicked", { event_id: eventId, variant });

    if (!links) {
      setLoading(true);
      try {
        const data = await getCalendarLinks(eventId, token);
        setLinks(data.calendar_links);
      } catch (err) {
        console.error("[AddToCalendar] Error:", err);
      } finally {
        setLoading(false);
      }
    }
    
    setIsOpen(!isOpen);
  }, [eventId, token, links, isOpen, variant]);

  const handleProviderSelect = useCallback((provider) => {
    track("calendar_provider_selected", { 
      event_id: eventId, 
      provider,
    });

    if (provider === "google" && links?.google) {
      window.open(links.google, "_blank");
      track("calendar_add_completed", { event_id: eventId, provider: "google" });
    } else if (provider === "outlook" && links?.outlook) {
      window.open(links.outlook, "_blank");
      track("calendar_add_completed", { event_id: eventId, provider: "outlook" });
    } else if (provider === "apple") {
      // Download ICS file
      const icsUrl = `${BACKEND_URL}/api/events/${eventId}/calendar/ics`;
      const link = document.createElement("a");
      link.href = icsUrl;
      link.download = links?.ics_filename || `unitylove-event.ics`;
      
      // Add auth header via fetch then blob
      fetch(icsUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((res) => res.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          track("calendar_add_completed", { event_id: eventId, provider: "apple" });
        })
        .catch((err) => console.error("[AddToCalendar] ICS download error:", err));
    }

    setIsOpen(false);
  }, [eventId, links, token]);

  // Variant styles
  const buttonStyles = {
    default: "px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/15 transition",
    compact: "px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm hover:bg-white/15 transition",
    inline: "text-white/60 hover:text-white text-sm underline-offset-2 hover:underline transition",
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main button */}
      <button
        onClick={handleToggle}
        disabled={loading}
        className={`flex items-center gap-2 ${buttonStyles[variant]}`}
      >
        <Calendar className={variant === "compact" ? "w-3.5 h-3.5" : "w-4 h-4"} />
        <span>{loading ? "Loading..." : "Add to Calendar"}</span>
        {variant !== "inline" && (
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && links && (
        <div className="absolute top-full left-0 mt-2 w-48 rounded-lg bg-gray-900 border border-white/20 shadow-xl z-50 overflow-hidden">
          {Object.values(PROVIDERS).map((provider) => (
            <button
              key={provider.id}
              onClick={() => handleProviderSelect(provider.id)}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/10 transition text-left"
            >
              <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${provider.color}`}>
                {provider.icon}
              </span>
              <span className="text-white text-sm">{provider.label}</span>
              <ExternalLink className="w-3 h-3 text-white/40 ml-auto" />
            </button>
          ))}
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Simple text link variant for inline use
 * Gets token from auth context automatically
 */
export function AddToCalendarLink({ eventId, className = "" }) {
  // Get token from localStorage (same as AuthContext uses)
  const token = localStorage.getItem("token");
  
  return (
    <AddToCalendar 
      eventId={eventId} 
      token={token} 
      variant="inline" 
      className={className}
    />
  );
}

/**
 * Success state component shown after ticket purchase or joining
 */
export function CalendarAddSuccess({ 
  eventId, 
  eventTitle,
  token,
  onClose,
}) {
  return (
    <div className="p-6 rounded-xl bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-500/30">
      <div className="text-center mb-4">
        <div className="w-12 h-12 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-3">
          <Calendar className="w-6 h-6 text-green-400" />
        </div>
        <h3 className="text-lg font-semibold text-white">You&apos;re in!</h3>
        <p className="text-sm text-white/60 mt-1">
          {eventTitle || "Your event is confirmed"}
        </p>
      </div>

      <AddToCalendar 
        eventId={eventId} 
        token={token}
        variant="default"
        className="w-full flex justify-center"
      />

      <p className="text-xs text-white/40 text-center mt-4">
        Most people who enjoy these events save them.
      </p>

      {onClose && (
        <button
          onClick={onClose}
          className="w-full mt-4 text-sm text-white/50 hover:text-white/70 transition"
        >
          Continue
        </button>
      )}
    </div>
  );
}
